import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Context, UndoOptions } from './type';
import { useNotificationsContext } from 'context';
import { UndoSnackbarContent } from 'app/components/snackbars';
import { GlobalErrorContext } from 'app/components/ReactContexts/globalErrorContext';

const UndoContext = createContext<Context | undefined>(undefined);

export function UndoProvider({ children }: { children: React.ReactNode }) {
  const { addErrorModal } = useContext(GlobalErrorContext);

  const { addSnackbar, removeSnackbar } = useNotificationsContext();
  const [isUndoLoading, setIsUndoLoading] = useState(false);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const currentUndoLogic = useRef<(() => Promise<void>) | null>(null);

  function clearTimer() {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }

  async function handleUndoClick() {
    if (currentUndoLogic.current) {
      try {
        await currentUndoLogic.current();
      } catch (error) {
        console.error('Undo action failed:', error);
        addErrorModal();
      }

      removeSnackbar();
    }
  }

  const executeAction = useCallback(
    async (options: UndoOptions) => {
      clearTimer();
      const delay = options.delayMs ?? 6000;

      if (options.strategy === 'IMMEDIATE') {
        try {
          await options.action();
        } catch (error) {
          console.error('Action failed:', error);
          addErrorModal();
          return;
        }

        currentUndoLogic.current = async () => {
          if (!options.undoAction) return;
          setIsUndoLoading(true);
          try {
            await options.undoAction();
          } finally {
            setIsUndoLoading(false);
            currentUndoLogic.current = null;
          }
        };

        addSnackbar(
          <UndoSnackbarContent
            description={options.label}
            onUndo={handleUndoClick}
          />,
          'screen-centered',
          delay,
        );
      } else {
        currentUndoLogic.current = async () => {
          clearTimer();
          currentUndoLogic.current = null;
        };

        addSnackbar(
          <UndoSnackbarContent
            description={options.label}
            onUndo={handleUndoClick}
          />,
          'screen-centered',
          delay,
        );

        timeoutRef.current = setTimeout(async () => {
          try {
            await options.action();
          } catch (error) {
            console.error('Action failed:', error);
            addErrorModal();
          }

          timeoutRef.current = null;
          currentUndoLogic.current = null;
        }, delay);
      }
    },
    [addSnackbar, removeSnackbar],
  );

  useEffect(() => {
    return () => clearTimer();
  }, []);

  return (
    <UndoContext.Provider value={{ executeAction, isUndoLoading }}>
      {children}
    </UndoContext.Provider>
  );
}

export function useUndo() {
  const context = useContext(UndoContext);
  if (!context) {
    throw new Error('useUndo must be used within an UndoProvider');
  }
  return context;
}
